// Endpoint definitions for Monitor ▸ Inventory; DTO → VM runs in transformResponse.
import { baseApi } from '@/lib/api/baseApi';
import { TAG_TYPES } from '@/lib/api/tags';
import { processServerError } from '@/lib/api/listQuery';
import { notifyOnSuccess } from '@/lib/api/notifyOnSuccess';
import {
  INVENTORY_LIST_QUERY,
  INVENTORY_ITEM_QUERY,
  MASS_BALANCE_SUMMARY_QUERY,
  PHYSICAL_RECONCILIATION_QUERY,
  MATERIAL_MOVEMENT_QUERY,
} from './inventory.queries';
import {
  CONTRACTS_STRIP_QUERY,
  SHIPMENTS_STRIP_QUERY,
  DECLARATIONS_STRIP_QUERY,
  ORDERS_STRIP_QUERY,
  OUTGOING_SHIPMENTS_STRIP_QUERY,
  ALLOCATIONS_STRIP_QUERY,
} from './inventoryStrips.queries';
import {
  buildInventoryQuery,
  processInventoryPage,
  processInventoryItem,
} from '../mappers/inventoryMappers';
import {
  processContractsStrip,
  processShipmentsStrip,
  processDeclarationsStrip,
  processOrdersStrip,
  processOutgoingShipmentsStrip,
  processAllocationsStrip,
} from '../mappers/inventoryStripMappers';
import {
  processMassBalanceSummary,
  processReconciliationSummary,
  processMaterialMovementSummary,
} from '../mappers/inventoryReportMappers';

// The one write here: a sub-period's physical count, always in the declaration unit.
const PHYSICAL_BALANCE_PATH = (module) => `/${module}/inventory/sub-period/physical-reconciliation`;

// Every strip is the same call shape, so they are declared from one table.
const STRIPS = [
  ['getContractsStrip', CONTRACTS_STRIP_QUERY, 'contractsSummary', processContractsStrip],
  ['getShipmentsStrip', SHIPMENTS_STRIP_QUERY, 'shipmentsSummary', processShipmentsStrip],
  [
    'getDeclarationsStrip',
    DECLARATIONS_STRIP_QUERY,
    'declarationsSummary',
    processDeclarationsStrip,
  ],
  ['getOrdersStrip', ORDERS_STRIP_QUERY, 'ordersSummary', processOrdersStrip],
  [
    'getOutgoingShipmentsStrip',
    OUTGOING_SHIPMENTS_STRIP_QUERY,
    'outgoingShipmentsSummary',
    processOutgoingShipmentsStrip,
  ],
  ['getAllocationsStrip', ALLOCATIONS_STRIP_QUERY, 'allocationsSummary', processAllocationsStrip],
];

const stripEndpoints = (build) =>
  Object.fromEntries(
    STRIPS.map(([name, document, field, process]) => [
      name,
      build.query({
        query: (variables) => ({ document, variables }),
        transformResponse: (data) => process(data?.[field]),
        providesTags: [{ type: TAG_TYPES.Inventory, id: 'SUMMARY' }],
      }),
    ]),
  );

export const inventoryApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getInventory: build.query({
      query: (screenState) => ({
        document: INVENTORY_LIST_QUERY,
        variables: buildInventoryQuery(screenState),
      }),
      transformResponse: (data) => processInventoryPage(data?.inventory),
      providesTags: (result) =>
        result
          ? [
              ...result.rows.map((row) => ({ type: TAG_TYPES.Inventory, id: row.id })),
              { type: TAG_TYPES.Inventory, id: 'LIST' },
            ]
          : [{ type: TAG_TYPES.Inventory, id: 'LIST' }],
    }),

    getInventoryItem: build.query({
      query: (variables) => ({ document: INVENTORY_ITEM_QUERY, variables }),
      transformResponse: (data) => processInventoryItem(data?.inventoryItem),
      providesTags: [{ type: TAG_TYPES.Inventory, id: 'DETAIL' }],
    }),

    getMassBalanceSummary: build.query({
      query: (variables) => ({ document: MASS_BALANCE_SUMMARY_QUERY, variables }),
      transformResponse: (data) => processMassBalanceSummary(data?.massBalanceSummary),
      providesTags: [{ type: TAG_TYPES.Inventory, id: 'MASS_BALANCE' }],
    }),

    getReconciliationSummary: build.query({
      query: (variables) => ({ document: PHYSICAL_RECONCILIATION_QUERY, variables }),
      transformResponse: (data) =>
        processReconciliationSummary(data?.physicalReconciliationSummary),
      providesTags: [{ type: TAG_TYPES.Inventory, id: 'RECONCILIATION' }],
    }),

    getMaterialMovementSummary: build.query({
      query: (variables) => ({ document: MATERIAL_MOVEMENT_QUERY, variables }),
      transformResponse: (data) => processMaterialMovementSummary(data?.materialMovementSummary),
      providesTags: [{ type: TAG_TYPES.Inventory, id: 'MOVEMENT' }],
    }),

    savePhysicalBalance: build.mutation({
      query: ({ module, subPeriodId, mbPeriodDetailId, quantity }) => ({
        url: PHYSICAL_BALANCE_PATH(module),
        method: 'PATCH',
        body: {
          sub_id: Number(subPeriodId),
          mb_period_id: Number(mbPeriodDetailId),
          physicalReconciliation: Number(quantity),
        },
      }),
      transformErrorResponse: processServerError,
      onQueryStarted: (arg, api) =>
        notifyOnSuccess(
          arg.isUpdate ? 'INVENTORY_PHYSICAL_BALANCE_UPDATED' : 'INVENTORY_PHYSICAL_BALANCE_ADDED',
        )(arg, api),
      invalidatesTags: [{ type: TAG_TYPES.Inventory, id: 'RECONCILIATION' }],
    }),

    ...stripEndpoints(build),
  }),
});

export const {
  useGetInventoryQuery,
  useGetInventoryItemQuery,
  useGetMassBalanceSummaryQuery,
  useGetReconciliationSummaryQuery,
  useGetMaterialMovementSummaryQuery,
  useSavePhysicalBalanceMutation,
  useGetContractsStripQuery,
  useGetShipmentsStripQuery,
  useGetDeclarationsStripQuery,
  useGetOrdersStripQuery,
  useGetOutgoingShipmentsStripQuery,
  useGetAllocationsStripQuery,
} = inventoryApi;
